/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {resolveStatusColors, StatusColorSets} from '@ion/dls/src/hooks/useStatusBadgeColors';

export enum JobStatus {
  PENDING = 'Pending',
  IN_PROGRESS = 'InProgress',
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  CANCELLED = 'Cancelled',
  INPROGRESS_CANCELLED = 'InProgressCancelled',
  CANCELLING = 'Cancelling',
  RETRY = 'Retry',
  SENDING = 'Sending',
  PARTIAL_SUCCESS = 'PartialSuccess',
  UNKNOWN = 'Unknown',
  ACKED = 'Acked',
  NACKED = 'Nacked'
};

/**
 * A resolved status badge: the label plus the 10% tint / saturated text pair.
 * Carried on a job notification so the panel row renders the same badge the
 * grid cell does without knowing anything about job statuses.
 */
export interface JobStatusBadge {
  color: string;
  label: string;
  textColor: string;
}

export const NEGATIVE_STATUSES: string[] = [JobStatus.FAILED, JobStatus.NACKED];
export const POSITIVE_STATUSES: string[] = [JobStatus.COMPLETED, JobStatus.ACKED, JobStatus.PARTIAL_SUCCESS];

export const CANCELLING_STATUSES: string[] = [
  JobStatus.CANCELLED,
  JobStatus.CANCELLING,
  JobStatus.INPROGRESS_CANCELLED
];

const statusLabels = (): Record<string, string> => ({
  [JobStatus.PENDING]              : translate('jobsearchengine:progressStatus.pending'),
  [JobStatus.IN_PROGRESS]          : translate('jobsearchengine:progressStatus.progress'),
  [JobStatus.COMPLETED]            : translate('jobsearchengine:progressStatus.completed'),
  [JobStatus.FAILED]               : translate('jobsearchengine:progressStatus.failed'),
  [JobStatus.CANCELLED]            : translate('jobsearchengine:progressStatus.cancelled'),
  [JobStatus.INPROGRESS_CANCELLED] : translate('jobsearchengine:progressStatus.inProgressCancelled'),
  [JobStatus.CANCELLING]           : translate('jobsearchengine:progressStatus.cancelling'),
  [JobStatus.RETRY]                : translate('jobsearchengine:progressStatus.retry'),
  [JobStatus.SENDING]              : translate('jobsearchengine:progressStatus.sending'),
  [JobStatus.ACKED]                : translate('jobsearchengine:progressStatus.acked'),
  [JobStatus.NACKED]               : translate('jobsearchengine:progressStatus.nacked'),
  [JobStatus.UNKNOWN]              : translate('jobsearchengine:progressStatus.unknown'),
  [JobStatus.PARTIAL_SUCCESS]      : translate('jobsearchengine:progressStatus.partialSuccess')
});

/**
 * The translated label for a status, falling back to the raw value so an
 * unmapped status from the service still reads as itself rather than blank.
 */
export const jobStatusLabel = (status?: string): string => (
  statusLabels()[status] || status || ''
);

const JOB_STATUS_SETS: StatusColorSets = {
  positiveStatuses : POSITIVE_STATUSES,
  negativeStatuses : NEGATIVE_STATUSES,
  pendingStatuses  : CANCELLING_STATUSES
};

/**
 * The label and colors a status renders as, matching the grid's status cell.
 *
 * ```typescript
 *
 *   import {jobStatusBadge} from './jobStatusBadge';
 *
 *   ...
 *   const badge = jobStatusBadge('Cancelled');
 *   // badge.color is the tint, badge.textColor the label color...
 *
 * ```
 */
export const jobStatusBadge = (status?: string): JobStatusBadge => ({
  ...resolveStatusColors(status, JOB_STATUS_SETS),
  label : jobStatusLabel(status)
});
